import {
  keyDomainFor,
  nextRefreshDelayMs,
  REFRESH_RETRY_DELAYS_MS,
  type SearchKeyDomain,
  shouldRefreshOnWake,
} from '@shared/core/search';
import type { SearchClient } from 'algoliasearch';
import { z } from 'zod';

const SEARCH_TOKEN_ENDPOINT = '/.redwood/functions/algoliaSearchToken';
const FAILURE_RETRY_MS = 30_000;

const credentialsSchema = z.object({
  engine: z.literal('algolia'),
  endpoint: z.string(),
  keys: z.object({ general: z.string(), orders: z.string().optional() }),
  expiresAt: z.string(),
});

export type AlgoliaCredentials = z.infer<typeof credentialsSchema>;

export async function fetchAlgoliaCredentials(getToken: () => Promise<string | null>): Promise<AlgoliaCredentials> {
  const jwt = await getToken();
  const response = await fetch(SEARCH_TOKEN_ENDPOINT, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'auth-provider': 'clerk',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  });
  if (!response.ok) throw new Error(`algoliaSearchToken responded ${response.status}`);
  return credentialsSchema.parse(await response.json());
}

const EMPTY_RESULT = {
  hits: [],
  nbHits: 0,
  page: 0,
  nbPages: 0,
  hitsPerPage: 0,
  exhaustiveNbHits: true,
  processingTimeMS: 0,
  query: '',
  params: '',
};

const EMPTY_FACET_RESULT = { facetHits: [], exhaustiveFacetsCount: true, processingTimeMS: 0 };

type Clients = Partial<Record<SearchKeyDomain, SearchClient>>;

export interface AlgoliaKeyManagerOptions {
  fetchCredentials: () => Promise<AlgoliaCredentials | null>;
  createClient: (apiKey: string) => SearchClient;
  now?: () => number;
  rand?: () => number;
}

export interface AlgoliaKeyManager {
  readonly client: SearchClient;
  domainClient: (domain: SearchKeyDomain) => SearchClient;
  start: () => () => void;
}

export function createAlgoliaKeyManager(options: AlgoliaKeyManagerOptions): AlgoliaKeyManager {
  const now = options.now ?? Date.now;
  const rand = options.rand ?? Math.random;

  let clients: Clients = {};
  let expiresAt: string | null = null;
  let inFlight: Promise<void> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const schedule = (delayMs: number) => {
    clearTimeout(timer);
    if (stopped) return;
    timer = setTimeout(() => {
      void load().catch(() => undefined);
    }, delayMs);
  };

  let failures = 0;

  const attempt = async (): Promise<void> => {
    try {
      const credentials = await options.fetchCredentials();
      if (!credentials) return;
      clients = {
        general: options.createClient(credentials.keys.general),
        orders: credentials.keys.orders ? options.createClient(credentials.keys.orders) : undefined,
      };
      expiresAt = credentials.expiresAt;
      failures = 0;
      schedule(nextRefreshDelayMs(credentials.expiresAt, now(), rand));
    } catch (error) {
      const backoffMs = REFRESH_RETRY_DELAYS_MS[failures];
      failures = backoffMs === undefined ? 0 : failures + 1;
      schedule(backoffMs ?? FAILURE_RETRY_MS);
      throw error instanceof Error ? error : new Error('Algolia search credentials unavailable');
    }
  };

  const load = (): Promise<void> => {
    inFlight ??= attempt().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  const ensureFresh = async (): Promise<void> => {
    if (inFlight) return inFlight;
    if (expiresAt === null || shouldRefreshOnWake(expiresAt, now())) return load();
  };

  const clientFor = async (domain: SearchKeyDomain): Promise<SearchClient | undefined> => {
    await ensureFresh();
    return clients[domain];
  };

  const route = async <TQuery extends { indexName: string }, TResult>(
    queries: readonly TQuery[],
    empty: TResult,
    run: (client: SearchClient, subset: TQuery[]) => Promise<TResult[]>,
    only?: SearchKeyDomain
  ): Promise<TResult[]> => {
    const results: TResult[] = queries.map(() => empty);
    const domains = new Set(queries.map((query) => keyDomainFor(query.indexName)));
    await Promise.all(
      [...domains].map(async (domain) => {
        if (only && domain !== only) return;
        const client = await clientFor(domain);
        if (!client) return;
        const positions = queries.flatMap((query, position) =>
          keyDomainFor(query.indexName) === domain ? [position] : []
        );
        const subset = await run(
          client,
          positions.map((position) => queries[position])
        );
        positions.forEach((position, offset) => {
          results[position] = subset[offset];
        });
      })
    );
    return results;
  };

  const buildClient = (only?: SearchKeyDomain): SearchClient =>
    ({
      search: async (queries: { indexName: string }[], requestOptions?: object) => {
        const results = await route(
          queries,
          EMPTY_RESULT as unknown,
          async (client, subset) =>
            (await client.search(subset as never, requestOptions as never)).results as unknown[],
          only
        );
        return { results };
      },
      searchForFacetValues: (queries: { indexName: string }[]) =>
        route(
          queries,
          EMPTY_FACET_RESULT as unknown,
          async (client, subset) => (await client.searchForFacetValues(subset as never)) as unknown[],
          only
        ),
      clearCache: async () => {
        await Promise.all(Object.values(clients).map((client) => client?.clearCache()));
      },
      addAlgoliaAgent: (segment: string, version?: string) => {
        Object.values(clients).forEach((client) => client?.addAlgoliaAgent(segment, version));
      },
    }) as unknown as SearchClient;

  const domainClients: Partial<Record<SearchKeyDomain, SearchClient>> = {};

  return {
    client: buildClient(),
    domainClient: (domain) => {
      const existing = domainClients[domain];
      if (existing) return existing;
      const created = buildClient(domain);
      domainClients[domain] = created;
      return created;
    },
    start: () => {
      stopped = false;
      const onWake = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        void ensureFresh().catch(() => undefined);
      };
      document.addEventListener('visibilitychange', onWake);
      window.addEventListener('online', onWake);
      void load().catch(() => undefined);
      return () => {
        stopped = true;
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onWake);
        window.removeEventListener('online', onWake);
      };
    },
  };
}
